import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ArrowUpRight, Info } from 'lucide-react';
import type { MetricInfo } from './MetricInfoModal';
import type { BreakdownItem } from './MetricBreakdownModal';
import { cn } from '../../lib/utils';

/** A labelled group of component amounts (e.g. "Today Due" → IPD/OPD/Pharmacy). */
export interface BreakdownSection {
  label: string;
  hint?: string;
  items: BreakdownItem[];
}

/** Breakdown shown when a KPI card is expanded. */
export interface KpiBreakdown {
  /** Optional one-line explanation shown above the component boxes. */
  description?: string;
  /** Flat component amounts that make up the KPI value. */
  items: BreakdownItem[];
  /** Optional grouped rows; when supplied they replace `items`. */
  sections?: BreakdownSection[];
  /** Lay sections out side by side in this many columns (default 1). */
  sectionColumns?: 1 | 2;
  /** Optional full-width total shown below the component rows. */
  total?: { label: string; value: string };
  /** Related module to open from the panel footer. Omit to hide the button. */
  route?: string;
  /** Footer button label, e.g. "Invoices". */
  linkLabel?: string;
  /** Optional in-page action (e.g. open a filtered list modal). */
  actionLabel?: string;
  onAction?: () => void;
}

export interface KpiStatCardProps {
  /** Card heading, also used as the info panel title. */
  label: string;
  /** Pre-formatted headline value. */
  value: string;
  /** Small secondary line under the value. */
  sub?: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon chip, e.g. "bg-warning/10 text-warning". */
  color: string;
  /** Bilingual "what/where" copy for the info (i) icon. */
  info: MetricInfo;
  /** When supplied (and non-empty), the card can be expanded to show its parts. */
  breakdown?: KpiBreakdown;
}

const INFO_LABELS = {
  en: { what: 'What is this?', source: 'Where it comes from' },
  bn: { what: 'এটি কী?', source: 'এটি কোথা থেকে আসে?' },
} as const;

const TOOLTIP_WIDTH = 320;
/** Used for the first placement pass, before the bubble has been measured. */
const TOOLTIP_ESTIMATED_HEIGHT = 220;

/**
 * KPI card used on the Billing home and Top Sheet.
 *
 * Everything stays inside the card — there are no modals:
 * - the component breakdown is always visible as a simple text list
 * - hovering (or focusing) the info (i) icon reveals a bilingual "what/where"
 *   tooltip, so the card footprint never changes
 *
 * The breakdown keeps each figure traceable to the ledgers that produced it
 * while staying compact enough to read at a glance.
 */
export function KpiStatCard({
  label,
  value,
  sub,
  icon: Icon,
  color,
  info,
  breakdown,
}: KpiStatCardProps) {
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipPos, setTipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const hasBreakdown = Boolean(
    breakdown &&
      (breakdown.items.length > 0 || (breakdown.sections?.length ?? 0) > 0),
  );

  /** Cancels a pending auto-hide. */
  const cancelHide = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  /** Center the bubble on the icon, flipping above it when there is no room below. */
  const place = useCallback(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) return;

    const width = Math.min(TOOLTIP_WIDTH, window.innerWidth - 16);
    const left = Math.min(
      Math.max(8, anchor.left + anchor.width / 2 - width / 2),
      Math.max(8, window.innerWidth - width - 8),
    );

    const height = bubbleRef.current?.offsetHeight ?? TOOLTIP_ESTIMATED_HEIGHT;
    let top = anchor.bottom + 8;
    if (top + height > window.innerHeight - 8) {
      top = anchor.top - height - 8;
    }
    setTipPos({ top: Math.max(8, top), left });
  }, []);

  const showTip = useCallback(() => {
    cancelHide();
    place();
    setTipOpen(true);
  }, [place, cancelHide]);
  const hideTip = useCallback(() => {
    cancelHide();
    setTipOpen(false);
  }, [cancelHide]);
  /** Small grace period so the pointer can travel from the icon into the bubble. */
  const hideTipSoon = useCallback(() => {
    cancelHide();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      setTipOpen(false);
    }, 140);
  }, [cancelHide]);

  useEffect(() => cancelHide, [cancelHide]);

  // Re-position once the real bubble height is known.
  useLayoutEffect(() => {
    if (!tipOpen) return;
    place();
  }, [tipOpen, place]);

  useEffect(() => {
    if (!tipOpen) return;
    const onScroll = (event: Event) => {
      // Scrolling inside the bubble itself should not close it.
      const target = event.target;
      if (bubbleRef.current && target instanceof Node && bubbleRef.current.contains(target)) return;
      hideTip();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideTip();
    };
    // Close on page scroll/resize so the bubble never detaches from its anchor.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', hideTip);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', hideTip);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [tipOpen, hideTip]);

  return (
    <div className="bg-surface p-4 rounded-xl border border-border shadow-sm transition-all text-left hover:border-primary/40">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="text-sm text-text-muted">{label}</span>
            <span
              ref={anchorRef}
              role="button"
              tabIndex={0}
              aria-label={`About ${label}`}
              aria-expanded={tipOpen}
              onMouseEnter={showTip}
              onMouseLeave={hideTipSoon}
              onFocus={showTip}
              onBlur={hideTipSoon}
              onClick={(event) => {
                // Focus reveals the tooltip; keep the click off any parent control.
                event.preventDefault();
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  setTipOpen((prev) => !prev);
                }
              }}
              className={cn(
                'inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full align-middle transition-colors hover:bg-primary/10 hover:text-primary',
                tipOpen ? 'bg-primary/10 text-primary' : 'text-text-muted',
              )}
            >
              <Info size={13} />
            </span>
          </span>

          <div className="mt-1">
            <p className="text-2xl font-bold text-text">{value}</p>
            {sub && <p className="text-[11px] text-text-muted mt-1">{sub}</p>}
          </div>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={20} />
        </div>
      </div>

      {/* Bilingual explanation — hover tooltip, replaces the old info modal. */}
      {tipOpen &&
        createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            style={{ position: 'fixed', top: tipPos.top, left: tipPos.left, width: TOOLTIP_WIDTH }}
            onMouseEnter={showTip}
            onMouseLeave={hideTipSoon}
            className="z-[80] max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-surface p-3 shadow-xl"
          >
            <p className="text-xs font-semibold text-text">{label}</p>
            <div className="mt-1.5 space-y-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {INFO_LABELS.en.what}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{info.en.what}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {INFO_LABELS.en.source}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{info.en.source}</p>
              </div>
            </div>
            <div className="my-2 h-px bg-border" />
            <div lang="bn" className="space-y-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {INFO_LABELS.bn.what}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{info.bn.what}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  {INFO_LABELS.bn.source}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{info.bn.source}</p>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Breakdown — always visible as a simple, modern text list. */}
      {hasBreakdown && breakdown && (
        <div className="mt-3 border-t border-border pt-3">
          {breakdown.description && (
            <p className="mb-2 text-[11px] leading-relaxed text-text-muted">
              {breakdown.description}
            </p>
          )}
          {breakdown.sections && breakdown.sections.length > 0 ? (
            <div
              className={cn(
                'gap-2',
                breakdown.sectionColumns === 2 ? 'grid grid-cols-2' : 'space-y-2',
              )}
            >
              {breakdown.sections.map((section) => (
                <div
                  key={section.label}
                  className="overflow-hidden rounded-lg border border-border/60 bg-background/40"
                >
                  <div className="flex items-baseline justify-between gap-2 border-b border-border/60 px-2.5 py-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      {section.label}
                    </p>
                    {section.hint && (
                      <span className="text-[10px] text-text-muted/80">{section.hint}</span>
                    )}
                  </div>
                  <dl className="divide-y divide-border/60">
                    {section.items.map((item) => (
                      <div
                        key={item.label}
                        className={cn(
                          'flex items-start justify-between gap-3 px-2.5 py-1.5',
                          item.emphasis && 'bg-primary/5',
                        )}
                      >
                        <div className="min-w-0">
                          <dt
                            className={cn(
                              'text-[11px] leading-snug',
                              item.emphasis ? 'font-semibold text-text' : 'text-text-muted',
                            )}
                          >
                            {item.label}
                          </dt>
                          {item.hint && (
                            <dd className="text-[10px] leading-snug text-text-muted/80">
                              {item.hint}
                            </dd>
                          )}
                        </div>
                        <dd
                          className={cn(
                            'shrink-0 whitespace-nowrap text-[11px] tabular-nums leading-snug',
                            item.emphasis ? 'font-bold text-primary' : 'font-medium text-text',
                          )}
                        >
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <dl className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60 bg-background/40">
              {breakdown.items.map((item) => (
                <div
                  key={item.label}
                  className={cn(
                    'flex items-start justify-between gap-3 px-2.5 py-1.5',
                    item.emphasis && 'bg-primary/5',
                  )}
                >
                  <div className="min-w-0">
                    <dt
                      className={cn(
                        'text-[11px] leading-snug',
                        item.emphasis ? 'font-semibold text-text' : 'text-text-muted',
                      )}
                    >
                      {item.label}
                    </dt>
                    {item.hint && (
                      <dd className="text-[10px] leading-snug text-text-muted/80">{item.hint}</dd>
                    )}
                  </div>
                  <dd
                    className={cn(
                      'shrink-0 whitespace-nowrap text-[11px] tabular-nums leading-snug',
                      item.emphasis ? 'font-bold text-primary' : 'font-medium text-text',
                    )}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {breakdown.total && (
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-2">
              <span className="text-[11px] font-semibold text-text">
                {breakdown.total.label}
              </span>
              <span className="shrink-0 text-sm font-bold tabular-nums text-primary">
                {breakdown.total.value}
              </span>
            </div>
          )}
          <div className="mt-2 space-y-1.5">
            {breakdown.onAction && (
              <button
                type="button"
                onClick={breakdown.onAction}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              >
                {breakdown.actionLabel || 'View list'}
                <ArrowUpRight size={13} />
              </button>
            )}
            {breakdown.route && (
              <button
                type="button"
                onClick={() => navigate(breakdown.route as string)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
              >
                Open {breakdown.linkLabel || 'module'}
                <ArrowUpRight size={13} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
